/**
 * The column header menu (spec M1-B4 §8.2, **captured at last in B10**).
 *
 * Round 1's capture landed on the help-centre container, so round 3 shipped 14 ASSUMED entries
 * (Q168) with eight of them disabled. `[c:column-menu]` finally has monday's, twice over, and
 * it is short: **248 px** wide, four 32 px rows —
 * `Rename · Wrap text · Sort · Add column to the right`.
 *
 * **6i re-checked it (q454).** The menu was re-opened twice more with a 2.6 s settle and its
 * live `role=menuitem` list printed each time: `Rename · Wrap text · Sort · Add column to the
 * right`, four rows, both times, with no submenu arrow. Four independent observations — it is
 * not rendering lazily; monday's column menu really is four rows.
 *
 * Ours keeps monday's four at the top, in monday's order, and then the entries this product
 * already implements below a divider. Each of those names, in its tooltip, **monday's own
 * equivalent path** for the same job — the toolbar `Filter`, `Hide` and `···` ▸ `Pin columns`
 * are all captured, and a column is reordered on monday by dragging its header, which ours
 * does too. `Settings` and `Delete column` are the two with **no captured monday path at all**
 * and are recorded as q457 rather than quietly dropped: removing them would make a status
 * column uneditable and a column undeletable.
 *
 * `Wrap text` is live since 6i: 6i's `column-wrap-text` capture measured what monday does
 * (the grid's row pitch goes **36 → 56 px** board-wide) and `wrappedColumnIds` is the
 * per-column view setting behind it (q455).
 *
 * **Round 10e** closed four defects here. `Rename` (P-080) and `Delete column` (P-087) open
 * dialogs that live on the **store**, because `Menu` closes this component in the same tick as
 * the row's `onSelect`; `Add column to the right` (P-082) carries its anchor instead of
 * appending at the far right; and `Duplicate column` · `Group by this column` ·
 * `Change column type` · `Collapse column` (P-084) were missing outright. The last two are
 * rendered **disabled with their reason**: `changeType` is a service this product does not have
 * and collapse is a view setting it does not have. What monday's own menu holds below its
 * fourth row is still uncaptured — **q504**.
 */
import { findColumnType, type ColumnId } from '@elizmonday/shared';
import { useTable, useTableActions } from '../context';
import { getColumnUi } from '../../columns/registry';
import { openColumnSettings } from '../../columns/settings-request';
import { NAME_COLUMN_ID } from '../store';
import { Menu, type MenuEntry } from './Menu';

export const COLUMN_MENU_WIDTH = 248;

export function ColumnMenu({ columnId, x, y }: { columnId: ColumnId; x: number; y: number }) {
  const payload = useTable((state) => state.payload);
  const effective = useTable((state) => state.effective);
  const actions = useTableActions();
  const column = payload?.columns.find((entry) => entry.id === columnId);
  if (!column) return null;

  const shared = findColumnType(column.type);
  const isName = column.id === NAME_COLUMN_ID;
  /** The name column is structural: monday greys the same five rows on it (B4 §4). */
  const nameReason = isName ? 'The name column is structural and cannot be renamed, moved, hidden or deleted.' : undefined;
  const pinned = effective.pinnedColumnIds.includes(column.id);
  const wrapped = effective.wrappedColumnIds.includes(column.id);
  const close = () => actions.openMenu(null);

  const entries: MenuEntry[] = [
    /* ── monday's four, in monday's order `[c:column-menu]` ─────────────── */
    {
      id: 'rename',
      label: 'Rename',
      disabled: isName,
      ...(nameReason ? { title: nameReason } : {}),
      /**
       * **P-080.** The dialog is the store's, not this component's. `Menu` calls `onClose()`
       * for every row without a submenu, `onClose` is `openMenu(null)`, and that unmounts
       * `ColumnMenu` in the same tick — so a `useState` here died before it could render and
       * `Rename` opened nothing at all. Double-clicking the header still worked, which is how
       * this survived every clickability sweep: the *row* was there and answered a click.
       */
      onSelect: () => actions.setRenamingColumn(columnId),
    },
    {
      id: 'wrap',
      label: 'Wrap text',
      'data-testid': 'column-menu-wrap',
      /** monday shows no tick, but a toggle with no state is unreadable off a live board. */
      ...(wrapped ? { hint: '✓' } : {}),
      onSelect: () => actions.wrapColumn(columnId, !wrapped),
    },
    {
      id: 'sort',
      label: 'Sort',
      submenu: [
        {
          id: 'sort-asc',
          label: 'Sort ascending',
          onSelect: () => actions.setEffective({ sort: [{ columnId, direction: 'asc' }] }),
        },
        {
          id: 'sort-desc',
          label: 'Sort descending',
          onSelect: () => actions.setEffective({ sort: [{ columnId, direction: 'desc' }] }),
        },
      ],
    },
    {
      id: 'add-right',
      label: 'Add column to the right',
      /** P-082: the gallery is anchored to *this* column; it used to append at the far right. */
      onSelect: () => actions.openAddColumnAfter(columnId),
    },

    /* ── ours, below the divider (registered deviation, spec §8) ────────── */
    { id: 'd1', label: '', divider: true },
    {
      id: 'settings',
      label: 'Settings',
      /** q457: monday's four-row menu has no column-settings row and no other captured path. */
      title: "Ours. monday's captured column menu has no settings row (q457).",
      disabled: shared === undefined,
      onSelect: () => {
        close();
        openColumnSettings(String(columnId));
      },
    },
    {
      id: 'filter',
      label: 'Filter by this column',
      title: "monday's equivalent path is the toolbar's Filter control.",
      disabled: (shared?.filterOperators.length ?? 0) === 0,
      onSelect: () => actions.openPane('quick-filter'),
    },
    {
      id: 'pin',
      label: pinned ? 'Unpin column' : 'Pin column',
      title: "monday's equivalent path is the toolbar ··· ▸ Pin columns.",
      disabled: isName,
      ...(nameReason ? { title: nameReason } : {}),
      onSelect: () => actions.pinColumn(columnId, !pinned),
    },
    {
      id: 'hide',
      label: 'Hide column',
      title: "monday's equivalent path is the toolbar's Hide control.",
      disabled: isName,
      ...(nameReason ? { title: nameReason } : {}),
      onSelect: () => actions.hideColumn(columnId, true),
    },
    {
      id: 'move',
      label: 'Move to',
      title: "monday's equivalent path is dragging the column header, which ours does too.",
      disabled: isName,
      ...(nameReason ? { title: nameReason } : {}),
      submenu: [
        {
          id: 'move-first',
          label: 'To the start',
          onSelect: () => {
            close();
            const first = payload?.columns.find((entry) => entry.id !== NAME_COLUMN_ID);
            void actions.moveColumn(columnId, first?.id ?? null);
          },
        },
        {
          id: 'move-last',
          label: 'To the end',
          onSelect: () => {
            close();
            void actions.moveColumn(columnId, null);
          },
        },
      ],
    },
    {
      id: 'duplicate',
      label: 'Duplicate column',
      /**
       * **P-084.** `columns.duplicate` shipped in round 2 and in `/v2` and had **no caller in
       * the product at all** — a column could be duplicated by an API client and not by a
       * person. The copy is `Dup. of <title>` beside the original, settings verbatim and no
       * cells; monday's own dialog asks whether to copy the values and was never captured
       * (**q504**, and the service's Q47).
       */
      title: "Ours. monday's captured column menu has no duplicate row (q504).",
      disabled: isName,
      ...(nameReason ? { title: nameReason } : {}),
      onSelect: () => void actions.duplicateColumn(columnId),
    },
    {
      id: 'group-by',
      label: 'Group by this column',
      title: "monday's equivalent path is the toolbar's Group by control.",
      disabled: isName || !getColumnUi(column.type).groupable,
      onSelect: () =>
        actions.setEffective({
          groupBy: { columnId, direction: 'asc', showEmptyGroups: false },
        }),
    },
    {
      id: 'change-type',
      label: 'Change column type',
      /**
       * **P-084.** Deliberately inert: converting a column needs `columns.changeType` and a
       * conversion table per type pair, which is a round of its own — the card says file it,
       * do not fake it. The row is here so the gap is visible rather than silent (**q504**).
       */
      disabled: true,
      title: 'Converting a column between types needs `columns.changeType` and a conversion table per type pair — not built yet (P-084, q504).',
    },
    {
      id: 'collapse',
      label: 'Collapse column',
      /** **P-084.** monday's narrow-column mode; there is no state for it anywhere yet (q504). */
      disabled: true,
      title: "monday's narrow-column mode has no view setting in this product yet (P-084, q504).",
    },
    {
      id: 'delete',
      label: 'Delete column',
      title: "Ours. monday's captured column menu has no delete row (q457).",
      danger: true,
      disabled: isName,
      ...(nameReason ? { title: nameReason } : {}),
      /** P-087: the confirm and the undo toast; `TableView` renders the dialog. */
      onSelect: () => actions.setDeletingColumn(columnId),
    },
  ];

  return (
    <Menu
      entries={entries}
      x={x}
      y={y}
      width={COLUMN_MENU_WIDTH}
      label={`${column.title} column menu`}
      onClose={close}
    />
  );
}
